import type { ChordQuality, PitchClass } from '@/types/music';

/** Semitone offsets from the root for each supported quality. */
export const QUALITY_INTERVALS: Record<ChordQuality, number[]> = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  dominant7: [0, 4, 7, 10],
  minor7: [0, 3, 7, 10],
  major7: [0, 4, 7, 11],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  diminished: [0, 3, 6],
  augmented: [0, 4, 8],
  power: [0, 7],
};

/** Suffix used when naming a blindly detected chord. */
export const QUALITY_SUFFIX: Record<ChordQuality, string> = {
  major: '',
  minor: 'm',
  dominant7: '7',
  minor7: 'm7',
  major7: 'maj7',
  sus2: 'sus2',
  sus4: 'sus4',
  diminished: 'dim',
  augmented: 'aug',
  power: '5',
};

/**
 * Qualities the blind detector considers. Deliberately smaller than the full
 * list: `augmented`, `diminished` and `power` are rare for beginners and adding
 * them mostly creates confusable neighbours for the common triads.
 */
export const DETECTABLE_QUALITIES: ChordQuality[] = [
  'major',
  'minor',
  'dominant7',
  'minor7',
  'major7',
  'sus2',
  'sus4',
];

export interface ChordTemplate {
  root: PitchClass;
  quality: ChordQuality;
  name: string;
  /** Binary 12-vector, already L2-normalised for cosine similarity. */
  vector: Float32Array;
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

function buildTemplate(root: PitchClass, quality: ChordQuality): ChordTemplate {
  const vector = new Float32Array(12);
  const intervals = QUALITY_INTERVALS[quality];
  for (const interval of intervals) vector[(root + interval) % 12] = 1;
  const norm = Math.sqrt(intervals.length);
  for (let i = 0; i < 12; i++) vector[i] /= norm;
  return {
    root,
    quality,
    name: NOTE_NAMES[root] + QUALITY_SUFFIX[quality],
    vector,
  };
}

let cachedBank: ChordTemplate[] | null = null;

/** All (root × detectable quality) templates. Built once. */
export function templateBank(): ChordTemplate[] {
  if (cachedBank) return cachedBank;
  const bank: ChordTemplate[] = [];
  for (let root = 0; root < 12; root++) {
    for (const quality of DETECTABLE_QUALITIES) {
      bank.push(buildTemplate(root as PitchClass, quality));
    }
  }
  cachedBank = bank;
  return bank;
}
